/**
 * 점주 콘솔 v2 엔트리.
 *
 * App.js에서 `/owner-v2/*` 라우트 하나로만 연결된다 (ProtectedRoute 안 — 미로그인은 /login).
 * - 자체 ThemeProvider (밝은 Mogoo 토큰) → 기존 관리자 테마에 영향 없음
 * - SessionProvider 가 backend 세션(GET /owner-console/session)을 로딩
 *   · 403 → 권한 없음 화면 / 실패 → 오류 화면(재시도) / dev·test 는 mock fallback
 * - 모든 라우트를 RequireAccess로 감싸 URL 직접 접근 시 권한 없음 화면 표시
 *   (UX용 — 실제 보안은 backend @Roles/@RequirePermissions 가드)
 * - 콘솔에 머무는 동안 탭 타이틀/파비콘을 "모구 파트너 콘솔"로 교체하고, 떠나면 복원
 */
import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { GlobalStyles, ThemeProvider } from '@mui/material';
import { ownerConsoleTheme } from './design-system/theme';
import { SessionProvider, useSession } from './permissions/SessionContext';
import OwnerConsoleLayout from './OwnerConsoleLayout';
import RequireAccess from './permissions/RequireAccess';
import ConsoleStatusScreen from './components/ConsoleStatusScreen';
import ErrorBoundary from '../components/ErrorBoundary';
import { CONSOLE_ROUTES } from './routes';
import { colors } from './design-system/tokens';

export const OWNER_CONSOLE_TITLE = '모구 파트너 콘솔';

// 브랜드 블루 라운드 사각형 + 흰색 'm' — 별도 에셋 없이 data URI 로 파비콘 교체
const OWNER_CONSOLE_FAVICON =
  'data:image/svg+xml,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">` +
      `<rect width="64" height="64" rx="14" fill="${colors.brand.primary}"/>` +
      `<text x="32" y="45" font-family="Arial, sans-serif" font-size="38" font-weight="800" fill="#fff" text-anchor="middle">m</text>` +
      `</svg>`,
  );

/** /owner-v2 에 머무는 동안만 탭 타이틀/파비콘 교체 — 관리자로 돌아가면 원복 */
function useOwnerConsoleDocumentMeta() {
  useEffect(() => {
    const prevTitle = document.title;
    const iconLink = document.querySelector("link[rel~='icon']");
    const prevIconHref = iconLink ? iconLink.getAttribute('href') : null;

    document.title = OWNER_CONSOLE_TITLE;
    if (iconLink) iconLink.setAttribute('href', OWNER_CONSOLE_FAVICON);

    return () => {
      document.title = prevTitle;
      if (iconLink && prevIconHref !== null) iconLink.setAttribute('href', prevIconHref);
    };
  }, []);
}

/** 세션 상태에 따라 로딩/거부/오류 화면 또는 콘솔 본체를 렌더링 */
const OwnerConsoleShell = () => {
  const { status, errorMessage, retry } = useSession();

  if (status === 'loading') return <ConsoleStatusScreen variant="loading" />;
  if (status === 'denied') return <ConsoleStatusScreen variant="denied" message={errorMessage} />;
  if (status === 'error') {
    return <ConsoleStatusScreen variant="error" message={errorMessage} onRetry={retry} />;
  }

  return (
    <Routes>
      <Route element={<OwnerConsoleLayout />}>
        {CONSOLE_ROUTES.map(({ Component, ...item }) => (
          <Route
            key={item.key}
            index={item.path === ''}
            path={item.path === '' ? undefined : item.path}
            element={
              <RequireAccess item={item}>
                <Component />
              </RequireAccess>
            }
          />
        ))}
        {/* 정산을 결제·정산 탭으로 통합했다. 기존 북마크는 권한 검증을 거쳐 보존한다. */}
        <Route
          path="settlements"
          element={
            <RequireAccess item={CONSOLE_ROUTES.find((item) => item.key === 'payments')}>
              <Navigate to="/owner-v2/payments?tab=settle" replace />
            </RequireAccess>
          }
        />
        {/* 정의되지 않은 경로는 대시보드로 */}
        <Route path="*" element={<Navigate to="/owner-v2" replace />} />
      </Route>
    </Routes>
  );
};

const OwnerConsoleApp = ({ initialSessionKey }) => {
  useOwnerConsoleDocumentMeta();

  return (
    <ThemeProvider theme={ownerConsoleTheme}>
      <GlobalStyles
        styles={{
          '@import':
            'url("https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/variable/pretendardvariable-dynamic-subset.min.css")',
          'html, body, #root': {
            backgroundColor: colors.background,
          },
          '*': {
            letterSpacing: 0,
          },
        }}
      />
      {/* 콘솔 전체 에러 격리 — 세션/레이아웃 크래시가 관리자 앱까지 번지지 않게 */}
      <ErrorBoundary>
        <SessionProvider initialSessionKey={initialSessionKey}>
          <OwnerConsoleShell />
        </SessionProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
};

export default OwnerConsoleApp;
